import React from 'react';
import { Table, Button, Space, Tag, Avatar, Tooltip } from 'antd';
import { EyeOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { format } from 'date-fns';

const ContactRequestTable = ({ requests, onView }) => {
  if (!requests || requests.length === 0) {
    return null;
  }

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Pending' },
      'in-progress': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'In Progress' },
      'resolved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Resolved' },
      'closed': { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: 'Closed' },
      'rejected': { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Rejected' },
    };
    return configs[status] || configs['pending'];
  };

  const personaLabels = {
    company: 'Company',
    association: 'Association',
    startup: 'Startup',
    incubator: 'Incubator',
    investor: 'Investor',
    academia: 'Academia',
    consultant: 'Consultant',
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Contact</span>,
      key: 'contact',
      render: (_, request) => {
        const name = request.contactName || 'Unknown';
        const firstLetter = name.charAt(0).toUpperCase();

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar
              size={40}
              style={{
                backgroundColor: '#e6d5f0',
                color: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '18px',
                fontWeight: 500
              }}
            >
              {firstLetter}
            </Avatar>
            <div>
              <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                {name}
              </div>
              <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
                {request.companyId?.companyName || 'Unknown Company'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Persona</span>,
      key: 'personaType',
      responsive: ['md'],
      render: (_, request) => (
        <Tag
          style={{
            fontSize: 13,
            fontFamily: "'Rubik', sans-serif",
            fontWeight: 500,
            border: '1px solid #d9d9d9',
            background: '#fafafa',
            color: '#470172',
            padding: '2px 12px',
            borderRadius: 6,
          }}
        >
          {personaLabels[request.personaType] || 'Company'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Subject</span>,
      key: 'subject',
      responsive: ['md'],
      render: (_, request) => (
        <div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#470172', marginBottom: '4px' }}>
            {request.subject}
          </div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
            {request.category}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Date</span>,
      key: 'date',
      responsive: ['lg'],
      render: (_, request) => (
        <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '12px', fontWeight: 400, color: '#8b8b8b' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : '—'}
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Status</span>,
      key: 'status',
      render: (_, request) => {
        const config = getStatusConfig(request.status);
        return (
          <Tag
            style={{
              fontSize: 13,
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 600,
              border: `1px solid ${config.border}`,
              background: config.bg,
              color: config.color,
              padding: '4px 14px',
              borderRadius: 6,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, request) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(request)}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#470172',
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={requests}
      rowKey="_id"
      scroll={{ x: 'max-content' }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            Total {total} requests
          </span>
        ),
        style: { fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }
      }}
      style={{ fontFamily: 'Rubik, sans-serif' }}
      locale={{
        emptyText: (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            No requests found
          </span>
        )
      }}
      rowClassName={() => 'hover:bg-gray-50'}
    />
  );
};

export default ContactRequestTable;
